import cx from 'classnames'
import { MouseEvent, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { useLinger } from 'hooks'
import {
  LingerContextSchemaData,
  logEvent,
  snowplowEvent,
  userToUserContextSchemaData,
} from 'lib/analytics'
import { ROUTE_PATH } from 'lib/paths'
import { User, UserFollowLogKey, UserProfileOpenLogKey } from 'lib/_types'

import { toggleBlockUserConfirmBox } from 'store/actions/confirmBox'

import useChatRoomLogic from 'features/Chat/hocs/useChatRoomLogic'
import { Link } from 'features/i18n/router'
import FollowButton from 'features/User/components/FollowButton'

import Menu from 'components/Menu'
import { useReportModal } from 'components/Modal/ReportModal/ReportModal'
import Avatar from '../Avatar'
import Nickname from '../Nickname'
import { HighlightText } from 'components'

export type UserListItem = {
  user: User
  itemIndex: number
  followCallbackSetter?: Function
  onClick?: (user: User, e: MouseEvent) => void
  actionButton?: (user: User, itemIndex: number) => ReactNode
  showBio?: boolean
  bioStyle?: 'balloon' | 'description'
  maxLine?: number
  showOnlineStatus?: boolean
  showFollowBackText?: boolean
  showDivider?: boolean
  showMenu?: boolean
  customDescription?: (user: User) => ReactNode
  from?: UserProfileOpenLogKey
  followFrom?: UserFollowLogKey
  nonclickable?: boolean
  hideNicknameTags?: boolean
  enableViewportCheck?: boolean
  onEnterViewport?: (
    user: User,
    index: number,
    params: LingerContextSchemaData
  ) => void
  onLeaveViewport?: (
    user: User,
    index: number,
    params: LingerContextSchemaData
  ) => void
  highlightText?: string[]
  clickUserCallback?: Function // used for UserList modals (UserFollowerModal, UserFollowingModal)
}

export const UserListItem = ({
  user,
  itemIndex,
  followCallbackSetter,
  onClick,
  actionButton,
  showBio,
  bioStyle = 'description',
  maxLine,
  showOnlineStatus,
  showFollowBackText,
  showDivider = true,
  showMenu,
  customDescription = null,
  from = 'undefined',
  followFrom = 'undefined',
  nonclickable,
  hideNicknameTags = false,
  enableViewportCheck,
  onEnterViewport,
  onLeaveViewport,
  highlightText,
  clickUserCallback = null,
}: UserListItem) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { onCreateChatRoom } = useChatRoomLogic()
  const reportModal = useReportModal()

  const { inViewRef } = useLinger({
    skip: !enableViewportCheck,
    enterCallback: (params) => {
      onEnterViewport?.(user, itemIndex, params)
    },
    leaveCallback: (params) => {
      onLeaveViewport?.(user, itemIndex, params)
    },
  })

  const linkProps = {
    className: nonclickable ? '--nonclickable' : '',
    to: ROUTE_PATH.USER(user.id),
    onClick: (e: MouseEvent) => {
      if (clickUserCallback) {
        clickUserCallback()
      }

      if (onClick || nonclickable) {
        e.preventDefault()
        return
      }

      logEvent.user_profile_open({
        target_user_id: user.id.toString(),
        from,
      })
    },
  }

  const menuOptions = useMemo(() => {
    return [
      {
        title: t('common.block'),
        onClick: () => dispatch(toggleBlockUserConfirmBox({ user })),
      },
      {
        title: t('common.report'),
        onClick: () =>
          reportModal.open({
            userId: user?.id?.toString?.(),
          }),
      },
      {
        title: t('common.chat'),
        onClick: () =>
          onCreateChatRoom({
            userId: user?.id,
            from: 'post',
          }),
      },
    ]
  }, [user])

  return (
    <div
      className={cx(
        'UserList__item',
        (!showBio || bioStyle !== 'description') && 'UserList__item--center',
        (bioStyle === 'balloon' || !showDivider) && 'UserList__item--no-divider'
      )}
      ref={inViewRef}
      onClick={(e) => onClick?.(user, e)}
    >
      <Link {...linkProps}>
        <div className="UserList__item__image_wrapper">
          <Avatar
            user={user}
            showOnlineStatus={showOnlineStatus}
            showVipCircle
          />
        </div>
      </Link>

      <Link className="UserList__item__name_wrapper" {...linkProps}>
        <Nickname user={user} hideTags={hideNicknameTags} />

        {showFollowBackText && user.followed_by && (
          <div className="UserList__item__description">
            {t('user_list.follow_back')}
          </div>
        )}

        {showBio && bioStyle === 'description' && (
          <div
            className={cx(
              'UserList__item__description',
              maxLine && `UserList__item__description--max-line-${maxLine}`
            )}
          >
            <HighlightText keywords={highlightText}>
              {user.biography}
            </HighlightText>
          </div>
        )}

        {customDescription && bioStyle === 'description' && (
          <div
            className={cx(
              'UserList__item__description',
              maxLine && `UserList__item__description--max-line-${maxLine}`
            )}
          >
            {customDescription(user)}
          </div>
        )}
      </Link>

      {typeof actionButton === 'function' ? (
        actionButton(user, itemIndex)
      ) : (
        <FollowButton
          user={user}
          followCallback={
            followCallbackSetter
              ? (_userId) => {
                  snowplowEvent.follow({
                    user: userToUserContextSchemaData({
                      user,
                      position: itemIndex,
                    }),
                  })

                  followCallbackSetter((prevState: any) => {
                    return prevState?.map?.((x: any) => {
                      if (x?.id == _userId) {
                        return {
                          ...x,
                          ...(x?.is_private
                            ? { follow_pending: true }
                            : { following: true }),
                        }
                      }

                      return x
                    })
                  })
                }
              : null
          }
          unfollowCallback={
            followCallbackSetter
              ? (_userId) => {
                  snowplowEvent.unfollow({
                    user: userToUserContextSchemaData({
                      user,
                      position: itemIndex,
                    }),
                  })

                  followCallbackSetter((prevState: any) => {
                    return prevState?.map?.((x: any) => {
                      if (x?.id == _userId) {
                        return {
                          ...x,
                          following: false,
                          follow_pending: false,
                        }
                      }

                      return x
                    })
                  })
                }
              : null
          }
          from={followFrom}
        />
      )}

      {showMenu && (
        <Menu
          arrowPosition="right"
          icon="3dots-v"
          onToggle={(_: any, e: any) => e.preventDefault()}
          options={menuOptions}
        />
      )}

      {showBio && bioStyle === 'balloon' && user.biography?.trim?.() && (
        <Link {...linkProps} className="UserList__item__balloon">
          <div
            className={cx(
              'UserList__item__description',
              maxLine && `UserList__item__description--max-line-${maxLine}`
            )}
          >
            <HighlightText keywords={highlightText}>
              {user.biography}
            </HighlightText>
          </div>
        </Link>
      )}
    </div>
  )
}
